import { Alert } from "@package/subframe/components/Alert";
import { Button } from "@package/subframe/components/Button";
import { Switch } from "@package/subframe/components/Switch";
import { FeatherBell, FeatherDownload } from "@subframe/core";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";

import { AcknowledgementTrackingTable } from "@/components/acknowledgements/acknowledgement-tracking-table";
import { AcknowledgementTrackingTiles } from "@/components/acknowledgements/acknowledgement-tracking-tiles";
import { Spinner } from "@/components/ui/spinner";
import {
  acknowledgementTrackingQueryControllerReport,
  useAcknowledgementTrackingQueryControllerGetItem,
  useAcknowledgementTrackingQueryControllerListItems,
  useManageAcknowledgementItemCommandControllerRemind,
  useManageAcknowledgementItemCommandControllerRemindAll,
} from "@/generated/acknowledgements/acknowledgements";
import {
  acknowledgementReportFilename,
  buildAcknowledgementReportCsv,
} from "@/lib/acknowledgements/acknowledgement-report-csv";
import { ACK_COPY } from "@/lib/acknowledgements/copy";
import { invalidateAcknowledgementQueries } from "@/lib/acknowledgements/invalidate-acknowledgement-queries";
import {
  filterRoster,
  remindableCount,
  REMINDER_ACTIONS_ENABLED,
  trackingHeaderLine,
} from "@/lib/acknowledgements/tracking";
import { extractApiErrorMessage } from "@/lib/compliance/invalidate-compliance-queries";

interface AcknowledgementTrackingPanelProps {
  nurseryId: string;
  /** The owning feature's row id — an event record id for incidents. */
  sourceId: string;
  itemType: string;
  /** Whether this viewer may chase people. */
  canSendReminders: boolean;
  /**
   * Heading, when the owning feature needs its own word for this.
   *
   * Defaults to "Acknowledgements", which is right for a risk assessment or a
   * policy: there, signing IS the obligation. It is wrong for an incident,
   * where the record has its own separate sign-off and these signatures close
   * nothing — so that caller overrides both of these.
   */
  title?: string;
  /** One line under the heading explaining what these signatures mean. */
  description?: string;
}

/**
 * The manager's view from the document's side (AC24–AC27).
 *
 * Self-contained on purpose: its only inputs are the item it is tracking and
 * whether the viewer can act. It owns its data, tiles, table, reminders,
 * export and invalidation, so Risk Hub and Policy Hub can drop it in unchanged
 * when they land — which is the "one set of records, two view directions" the
 * ticket is built around.
 */
export function AcknowledgementTrackingPanel({
  nurseryId,
  sourceId,
  itemType,
  canSendReminders,
  title = "Acknowledgements",
  description,
}: AcknowledgementTrackingPanelProps) {
  const queryClient = useQueryClient();
  const [outstandingOnly, setOutstandingOnly] = useState(false);
  const [remindingRequestId, setRemindingRequestId] = useState<string | null>(
    null,
  );
  const [isExporting, setIsExporting] = useState(false);

  // Find the current version for this item. Cheaper than a dedicated endpoint,
  // and the list already carries the progress tiles.
  const itemsQuery = useAcknowledgementTrackingQueryControllerListItems(
    nurseryId,
    { itemType: itemType as never, sourceId, limit: 1 },
    { query: { refetchOnMount: "always" } },
  );
  const currentVersion = itemsQuery.data?.data.items?.[0];

  const detailQuery = useAcknowledgementTrackingQueryControllerGetItem(
    currentVersion?.itemVersionId ?? "",
    {
      query: {
        enabled: Boolean(currentVersion?.itemVersionId),
        refetchOnMount: "always",
      },
    },
  );

  const remindOne = useManageAcknowledgementItemCommandControllerRemind();
  const remindAll = useManageAcknowledgementItemCommandControllerRemindAll();

  const detail = detailQuery.data?.data;

  if (itemsQuery.isLoading) {
    return (
      <div className="flex w-full items-center justify-center py-8">
        <Spinner />
      </div>
    );
  }

  // Nothing published yet is the normal case, not an error — most records are
  // never sent out for acknowledgement.
  if (!currentVersion) {
    return null;
  }

  async function handleRemind(requestId: string) {
    setRemindingRequestId(requestId);
    try {
      await remindOne.mutateAsync({ requestId });
      toast.success(ACK_COPY.TOAST_REMINDED);
      await invalidateAcknowledgementQueries(queryClient);
    } catch (error) {
      toast.error(extractApiErrorMessage(error, ACK_COPY.TOAST_REMIND_ERROR));
    } finally {
      setRemindingRequestId(null);
    }
  }

  async function handleRemindAll() {
    if (!currentVersion) return;
    try {
      const result = await remindAll.mutateAsync({
        itemVersionId: currentVersion.itemVersionId,
      });
      const { reminded, skipped } = result.data;
      toast.success(
        skipped > 0
          ? `Reminded ${reminded}. ${skipped} were reminded recently and were skipped.`
          : `Reminded ${reminded}.`,
      );
      await invalidateAcknowledgementQueries(queryClient);
    } catch (error) {
      toast.error(extractApiErrorMessage(error, ACK_COPY.TOAST_REMIND_ERROR));
    }
  }

  /**
   * Fetches the WHOLE report before building the file.
   *
   * Deliberately not built from the roster already on screen: that would export
   * whatever the "outstanding only" toggle happened to be showing, and silently
   * truncating a compliance report is the worst failure this feature could have.
   */
  async function handleExport() {
    if (!currentVersion) return;
    setIsExporting(true);
    try {
      const response = await acknowledgementTrackingQueryControllerReport(
        currentVersion.itemVersionId,
      );
      const rows = response.data.rows ?? [];
      if (rows.length === 0) {
        toast.info(ACK_COPY.TOAST_EXPORT_EMPTY);
        return;
      }

      const blob = new Blob([buildAcknowledgementReportCsv(rows)], {
        type: "text/csv;charset=utf-8",
      });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = acknowledgementReportFilename(
        currentVersion.title,
        currentVersion.version,
      );
      anchor.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(extractApiErrorMessage(error, "Could not export the report"));
    } finally {
      setIsExporting(false);
    }
  }

  const roster = detail?.roster ?? [];
  const remindable = remindableCount(roster, new Date());

  return (
    <section
      className="flex w-full flex-col items-start gap-4 rounded-md border border-neutral-200 p-4"
      data-testid="ack-tracking-panel"
    >
      <div className="flex w-full flex-wrap items-center justify-between gap-2">
        <div className="flex flex-col">
          <span className="text-heading-3 font-heading-3 text-default-font">
            {title}
          </span>
          {description && (
            <span className="text-body font-body text-subtext-color">
              {description}
            </span>
          )}
          <span className="text-caption font-caption text-subtext-color">
            {trackingHeaderLine(currentVersion)}
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {REMINDER_ACTIONS_ENABLED && canSendReminders && (
            <Button
              variant="neutral-secondary"
              icon={<FeatherBell />}
              disabled={remindable === 0 || remindAll.isPending}
              loading={remindAll.isPending}
              onClick={handleRemindAll}
              data-testid="ack-remind-all"
            >
              {remindable === 0
                ? "No one to remind"
                : `Remind ${remindable} ${remindable === 1 ? "person" : "people"}`}
            </Button>
          )}
          <Button
            variant="neutral-secondary"
            icon={<FeatherDownload />}
            loading={isExporting}
            onClick={handleExport}
            data-testid="ack-export"
          >
            Export
          </Button>
        </div>
      </div>

      <AcknowledgementTrackingTiles progress={currentVersion.progress} />

      {/* Goes with the buttons: this copy exists only to explain what pressing
          Remind does, so it is noise once there is nothing to press. */}
      {REMINDER_ACTIONS_ENABLED && canSendReminders && (
        <Alert variant="neutral" description={ACK_COPY.REMIND_HELP} />
      )}

      <div className="flex w-full items-center justify-end gap-2">
        <span className="text-caption font-caption text-subtext-color">
          Outstanding only
        </span>
        <Switch
          checked={outstandingOnly}
          onCheckedChange={setOutstandingOnly}
          data-testid="ack-outstanding-only"
        />
      </div>

      {detailQuery.isLoading ? (
        <div className="flex w-full items-center justify-center py-8">
          <Spinner />
        </div>
      ) : roster.length === 0 ? (
        <span className="text-body font-body text-subtext-color">
          Nobody has been asked to acknowledge this yet.
        </span>
      ) : (
        <AcknowledgementTrackingTable
          roster={filterRoster(roster, outstandingOnly)}
          canSendReminders={canSendReminders}
          remindingRequestId={remindingRequestId}
          onRemind={handleRemind}
        />
      )}
    </section>
  );
}
